import React from 'react';
import { ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { ExpenseExtractSuggestions } from '../types';

interface ImportReviewPanelProps {
  title?: string;
  suggestions: ExpenseExtractSuggestions;
  fields: Array<{
    key: keyof ExpenseExtractSuggestions | string;
    label: string;
    value: string | number | null | undefined;
    onChange: (val: string) => void;
    type?: 'text' | 'number' | 'date';
  }>;
  confidence?: number | null;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
  loading?: boolean;
}

export const ImportReviewPanel: React.FC<ImportReviewPanelProps> = ({
  title = 'Revisión IA',
  suggestions,
  fields,
  confidence,
  onConfirm,
  onCancel,
  confirmLabel = 'Guardar gasto',
  loading = false
}) => {
  const lowConfidence = confidence != null && confidence < 70;
  const needsReview = suggestions.revisionRequerida || lowConfidence;

  return (
    <aside className="w-full lg:w-96 border-l border-gray-200 bg-gray-50 p-4 flex flex-col gap-4 overflow-y-auto max-h-[70vh]">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600">Revise los datos extraídos antes de guardar.</p>
      </div>

      {needsReview && (
        <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
          <span>
            {suggestions.message ||
              'Confianza baja o datos incompletos. Verifique importes y fecha.'}
          </span>
        </div>
      )}

      {confidence != null && (
        <div className="text-sm">
          <span className="text-gray-600">Confianza IA: </span>
          <span className={lowConfidence ? 'text-amber-600 font-medium' : 'text-green-600 font-medium'}>
            {confidence}%
          </span>
        </div>
      )}

      <div className="space-y-3 flex-1">
        {fields.map((f) => (
          <div key={String(f.key)}>
            <label className="block text-xs font-medium text-gray-700 mb-1">{f.label}</label>
            <input
              type={f.type || 'text'}
              value={f.value ?? ''}
              onChange={(e) => f.onChange(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        ))}
      </div>

      <div className="flex gap-2 pt-2 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-white"
        >
          Volver
        </button>
        <button
          type="button"
          disabled={loading}
          onClick={onConfirm}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          <CheckCircleIcon className="h-4 w-4" />
          {loading ? 'Guardando…' : confirmLabel}
        </button>
      </div>
    </aside>
  );
};
